import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, CreditCard, Rocket, Zap } from 'lucide-react';
import axios from 'axios';
import API from '@/utils/api';

export default function SubscriptionBar() {
  const [sub, setSub] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/subscription/status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setSub(res.data)).catch(() => {});
  }, [token]);

  if (!sub) return null;

  // Trial active
  if (sub.is_trial && sub.active) {
    return (
      <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex items-center justify-center gap-3 text-[12px]" data-testid="sub-bar-trial">
        <Shield className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
        <span className="text-blue-700">
          <b>Okres próbny</b> — {sub.trial_days_remaining} dni · {sub.trial_publications_used}/{sub.trial_publications_limit} publikacji
        </span>
      </div>
    );
  }

  // Trial expired
  if (sub.is_trial && !sub.active) {
    return (
      <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center justify-center gap-3 text-[12px]" data-testid="sub-bar-trial-expired">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
        <span className="text-amber-700">
          <b>System wstrzymany</b> — {sub.trial_publications_exhausted ? 'wykorzystano limit publikacji' : 'minęło 7 dni'}
        </span>
        <button onClick={() => navigate('/panel/subskrypcja')} className="px-3 py-1 rounded-lg bg-[#F47A3B] text-white text-[11px] font-bold hover:bg-[#E06A2E] transition-colors" data-testid="sub-bar-upgrade">
          Zacznij teraz
        </button>
      </div>
    );
  }

  // Active - normal
  if (sub.active && sub.days_remaining > 7) {
    return (
      <div className="bg-emerald-50 border-b border-emerald-100 px-4 py-2 flex items-center justify-center gap-3 text-[12px]" data-testid="sub-bar-active">
        <Zap className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        <span className="text-emerald-700">
          <b>System aktywny</b> — zdobywasz klientów · {sub.days_remaining} dni do odnowienia
          {sub.locked_price && <span className="ml-2 text-emerald-500">| Cena {sub.locked_price} zł zablokowana</span>}
        </span>
      </div>
    );
  }

  // Active - expiring soon (<7 days)
  if (sub.active && sub.days_remaining <= 7 && sub.days_remaining > 0) {
    return (
      <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center justify-center gap-3 text-[12px]" data-testid="sub-bar-expiring">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
        <span className="text-amber-700">
          <b>Zostało {sub.days_remaining} dni</b> — subskrypcja odnowi się automatycznie
        </span>
        <button onClick={() => navigate('/panel/subskrypcja')} className="px-3 py-1 rounded-lg bg-amber-500 text-white text-[11px] font-bold hover:bg-amber-600 transition-colors" data-testid="sub-bar-check">
          Sprawdź
        </button>
      </div>
    );
  }

  // Payment failed
  if (sub.status === 'past_due' || sub.status === 'failed') {
    return (
      <div className="bg-red-50 border-b border-red-100 px-4 py-2 flex items-center justify-center gap-3 text-[12px]" data-testid="sub-bar-failed">
        <CreditCard className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
        <span className="text-red-700"><b>Płatność nie powiodła się</b> — system może zostać wstrzymany</span>
        <button onClick={() => navigate('/panel/subskrypcja')} className="px-3 py-1 rounded-lg bg-red-500 text-white text-[11px] font-bold hover:bg-red-600 transition-colors" data-testid="sub-bar-fix">
          Uzupełnij płatność
        </button>
      </div>
    );
  }

  // No subscription — THE KEY CHANGE
  if (!sub.active) {
    return (
      <div className="bg-gradient-to-r from-[#F47A3B]/10 to-amber-50 border-b border-[#F47A3B]/20 px-4 py-2 flex items-center justify-center gap-3 text-[12px]" data-testid="sub-bar-none">
        <Rocket className="w-3.5 h-3.5 text-[#F47A3B] flex-shrink-0" />
        <span className="text-zinc-700">Aby zdobywać klientów — <b>aktywuj konto</b></span>
        <button onClick={() => navigate('/panel/subskrypcja')} className="px-3 py-1 rounded-lg bg-[#F47A3B] text-white text-[11px] font-bold hover:bg-[#E06A2E] transition-colors" data-testid="sub-bar-activate">
          Zacznij teraz
        </button>
      </div>
    );
  }

  return null;
}
